import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { TrendingDown, Plus, X, Save } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const parseEuroCent = (value) => {
  if (!value) return 0;
  const str = String(value).trim();
  const normalized = str.replace(',', '.');
  const num = parseFloat(normalized);
  return isNaN(num) ? 0 : Math.round(num * 100) / 100;
};

const formatEuro = (value) => {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value || 0);
};

const ManuelleEingabeTab = ({ onExpenseAdded }) => {
  const [manualEntry, setManualEntry] = useState({
    lieferant: "",
    datum: new Date().toISOString().split('T')[0],
    kategorie: "Wareneinkauf",
    positionen: [{ artikel: "", menge: 1, einheit: "Stueck", preis: "" }],
    notiz: ""
  });

  const addPosition = () => {
    setManualEntry({
      ...manualEntry,
      positionen: [...manualEntry.positionen, { artikel: "", menge: 1, einheit: "Stueck", preis: "" }]
    });
  };

  const updatePosition = (index, field, value) => {
    const newPositionen = [...manualEntry.positionen];
    newPositionen[index] = { ...newPositionen[index], [field]: value };
    setManualEntry({ ...manualEntry, positionen: newPositionen });
  };

  const removePosition = (index) => {
    if (manualEntry.positionen.length <= 1) return;
    setManualEntry({
      ...manualEntry,
      positionen: manualEntry.positionen.filter((_, i) => i !== index)
    });
  };

  const calculateTotal = () => {
    return manualEntry.positionen.reduce((sum, pos) => {
      return sum + (parseEuroCent(pos.preis) * (parseFloat(pos.menge) || 1));
    }, 0);
  };

  const saveManualEntry = async () => {
    if (!manualEntry.lieferant) {
      toast.error("Bitte Lieferant angeben");
      return;
    }

    const validPositionen = manualEntry.positionen.filter(p => p.artikel && p.preis);
    if (validPositionen.length === 0) {
      toast.error("Mindestens eine Position mit Artikel und Preis erforderlich");
      return;
    }

    try {
      await axios.post(`${API}/ausgaben/erfassen`, {
        lieferant: manualEntry.lieferant,
        datum: manualEntry.datum,
        kategorie: manualEntry.kategorie,
        positionen: validPositionen.map(p => ({
          artikel: p.artikel,
          menge: parseFloat(p.menge) || 1,
          einheit: p.einheit,
          einzelpreis: parseEuroCent(p.preis),
          gesamtpreis: parseEuroCent(p.preis) * (parseFloat(p.menge) || 1)
        })),
        notiz: manualEntry.notiz,
        gesamtbetrag: calculateTotal()
      }, { withCredentials: true });

      toast.success(`Ausgabe erfasst: ${formatEuro(calculateTotal())}`);

      setManualEntry({
        lieferant: "",
        datum: new Date().toISOString().split('T')[0],
        kategorie: "Wareneinkauf",
        positionen: [{ artikel: "", menge: 1, einheit: "Stueck", preis: "" }],
        notiz: ""
      });

      onExpenseAdded?.();
    } catch (error) {
      toast.error("Fehler: " + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <TrendingDown className="text-red-400" />
          Ausgabe manuell erfassen
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="text-sm text-slate-400 block mb-1">Lieferant *</label>
            <Input
              value={manualEntry.lieferant}
              onChange={(e) => setManualEntry({ ...manualEntry, lieferant: e.target.value })}
              placeholder="z.B. METRO, Edeka, Getranke Mueller"
              className="bg-slate-700 border-slate-600"
              data-testid="input-lieferant"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 block mb-1">Datum</label>
            <Input
              type="date"
              value={manualEntry.datum}
              onChange={(e) => setManualEntry({ ...manualEntry, datum: e.target.value })}
              className="bg-slate-700 border-slate-600"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 block mb-1">Kategorie</label>
            <Select value={manualEntry.kategorie} onValueChange={(v) => setManualEntry({ ...manualEntry, kategorie: v })}>
              <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Wareneinkauf">Wareneinkauf</SelectItem>
                <SelectItem value="Getraenke">Getraenke</SelectItem>
                <SelectItem value="Lebensmittel">Lebensmittel</SelectItem>
                <SelectItem value="Verbrauchsmaterial">Verbrauchsmaterial</SelectItem>
                <SelectItem value="Reinigung">Reinigung</SelectItem>
                <SelectItem value="Reparatur">Reparatur</SelectItem>
                <SelectItem value="Sonstiges">Sonstiges</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm text-slate-400 block mb-1">Notiz (optional)</label>
            <Input
              value={manualEntry.notiz}
              onChange={(e) => setManualEntry({ ...manualEntry, notiz: e.target.value })}
              placeholder="z.B. Wochenlieferung"
              className="bg-slate-700 border-slate-600"
            />
          </div>
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm text-slate-400">Positionen</label>
            <Button size="sm" variant="outline" onClick={addPosition} className="border-slate-600">
              <Plus size={14} className="mr-1" /> Position
            </Button>
          </div>

          <div className="space-y-2">
            {manualEntry.positionen.map((pos, idx) => (
              <div key={idx} className="flex items-center gap-2 p-3 bg-slate-700/50 rounded-lg">
                <Input
                  value={pos.artikel}
                  onChange={(e) => updatePosition(idx, 'artikel', e.target.value)}
                  placeholder="Artikel"
                  className="flex-1 bg-slate-600 border-slate-500"
                />
                <Input
                  type="number"
                  value={pos.menge}
                  onChange={(e) => updatePosition(idx, 'menge', e.target.value)}
                  placeholder="Menge"
                  className="w-20 bg-slate-600 border-slate-500"
                />
                <Select value={pos.einheit} onValueChange={(v) => updatePosition(idx, 'einheit', v)}>
                  <SelectTrigger className="w-24 bg-slate-600 border-slate-500"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Stueck">Stueck</SelectItem>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="g">g</SelectItem>
                    <SelectItem value="l">Liter</SelectItem>
                    <SelectItem value="ml">ml</SelectItem>
                    <SelectItem value="Kiste">Kiste</SelectItem>
                    <SelectItem value="Packung">Packung</SelectItem>
                  </SelectContent>
                </Select>
                <div className="relative">
                  <Input
                    value={pos.preis}
                    onChange={(e) => updatePosition(idx, 'preis', e.target.value)}
                    placeholder="12,90"
                    className="w-28 bg-slate-600 border-slate-500 pr-8"
                    data-testid={`input-preis-${idx}`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">EUR</span>
                </div>
                <span className="text-green-400 font-mono w-24 text-right">
                  {formatEuro(parseEuroCent(pos.preis) * (parseFloat(pos.menge) || 1))}
                </span>
                {manualEntry.positionen.length > 1 && (
                  <Button size="sm" variant="ghost" onClick={() => removePosition(idx)} className="text-red-400">
                    <X size={14} />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-slate-700">
          <div>
            <span className="text-slate-400">Gesamtbetrag:</span>
            <span className="text-2xl font-bold text-red-400 ml-3">{formatEuro(calculateTotal())}</span>
          </div>
          <Button onClick={saveManualEntry} className="bg-red-600 hover:bg-red-700" data-testid="btn-save-manual">
            <Save size={16} className="mr-2" /> Ausgabe speichern
          </Button>
        </div>
      </div>

      <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
        <h3 className="text-slate-300 font-medium mb-2">Preiseingabe (Euro.Cent)</h3>
        <p className="text-slate-400 text-sm">
          Preise koennen als <code className="bg-slate-700 px-1 rounded">12.90</code> oder <code className="bg-slate-700 px-1 rounded">12,90</code> eingegeben werden.
          Beide Formate werden automatisch erkannt und korrekt verarbeitet.
        </p>
      </div>
    </div>
  );
};

export default ManuelleEingabeTab;
